import React, { useEffect, useRef, useState, useMemo, useCallback, memo } from "react";
import { motion } from "framer-motion";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { setFilter } from "../../store/reducers/stateReducer";
import { setDataLayer } from "../../store/reducers/helpersReducer";
import { selectDataLayersViewFidersDistances } from "../../store/selectors/helpersSelector";
import { selectDialFilter } from "../../store/selectors/supervisionSelector";

// const statuses = [
//     { label: "Все", color: "#9ca3af", types: ['pillar04', 'pillar04_accounting_not_completed', 'pillar04_accounting_half_completed', 'pillar04_accounting_rejected', 'pillar04_accounting_completed', 'pillar04_accounting_ready'] },
//     { label: "Актуальные", color: "#fb923c", types: ['pillar04_accounting_not_completed', 'pillar04_accounting_half_completed'] },
//     { label: "Не устранено", color: "#f87171", types: ['pillar04_accounting_not_completed'] },
//     { label: "Частично", color: "#fcd34d", types: ['pillar04_accounting_half_completed'] },
//     { label: "Отклонено", color: "#a78bfa", types: ['pillar04_accounting_rejected'] },
//     { label: "Устранено", color: "#34d399", types: ['pillar04_accounting_completed'] },
//     { label: "Готово", color: "#3b82f6", types: ['pillar04_accounting_ready'] }
// ];

export const statuses = [
    { label: "Все", color: "var(--dial-all)", types: ['default', 'accounting_not_completed', 'accounting_half_completed', 'accounting_rejected', 'accounting_completed', 'accounting_ready', 'accounting_approved'] },
    { label: "Актуальные", color: "var(--pillar04_accounting_actual)", types: ['accounting_not_completed', 'accounting_half_completed'] },
    { label: "Не устранено", color: "var(--accounting_not_completed)", types: ['accounting_not_completed'] },
    { label: "Частично", color: "var(--accounting_half_completed)", types: ['accounting_half_completed'] },
    { label: "Отклонено", color: "var(--accounting_rejected)", types: ['accounting_rejected'] },
    // { label: "Согласовано", color: "var(--pillar04_accounting_approved)", types: ['pillar04_accounting_approved'] },
    { label: "Устранено", color: "var(--accounting_completed)", types: ['accounting_completed'] },
    { label: "Готово", color: "var(--accounting_ready)", types: ['accounting_ready'] },

];

const sectorAngle = 360 / statuses.length;

const StatusDialControl = () => {
    const isMounted = useRef(false);
    const dispatch = useDispatch()

    const showOtherLabelsRedux = useSelector(selectDataLayersViewFidersDistances)
    const dialFilterRedux = useSelector(selectDialFilter)


    const [selectedIndex, setSelectedIndex] = useState(0);
    const [hoverIndex, setHoverIndex] = useState(null);
    const [showSectorLabels, setShowSectorLabels] = useState(true);
    const [showOtherLabels, setShowOtherLabels] = useState(false);
    const [ringOpacityLevel, setRingOpacityLevel] = useState(Array(statuses.length).fill(1));
    const [expand, setExpand] = useState(null)
    const [expandTm, setExpandTm] = useState(null)
    const [ringAnimationState, setRingAnimationState] = useState(Array(statuses.length).fill(1));

    // Мемоизируем размеры экрана
    const isLargeScreen = useMemo(() => window.innerWidth > 768, []);

    // Создаем статические SVG элементы один раз
    const staticElements = useMemo(() => {
        const innerRadius = 30;
        const outerRadius = 120;
        const center = 160;
        const ringInner = outerRadius + 12;
        const ringOuter = outerRadius + 24;

        return statuses.map((status, i) => {
            const angleDeg = sectorAngle * i - 90 + sectorAngle / 2;
            const angleRad = (angleDeg * Math.PI) / 180;
            const startAngle = sectorAngle * i - 90;
            const endAngle = startAngle + sectorAngle;

            // Предвычисляем тригонометрические значения
            const cosAngleRad = Math.cos(angleRad);
            const sinAngleRad = Math.sin(angleRad);
            const cosStartAngle = Math.cos((Math.PI * startAngle) / 180);
            const sinStartAngle = Math.sin((Math.PI * startAngle) / 180);
            const cosEndAngle = Math.cos((Math.PI * endAngle) / 180);
            const sinEndAngle = Math.sin((Math.PI * endAngle) / 180);

            // Статические координаты без offset
            const x1 = center + innerRadius * cosStartAngle;
            const y1 = center + innerRadius * sinStartAngle;
            const x2 = center + outerRadius * cosStartAngle;
            const y2 = center + outerRadius * sinStartAngle;
            const x3 = center + outerRadius * cosEndAngle;
            const y3 = center + outerRadius * sinEndAngle;
            const x4 = center + innerRadius * cosEndAngle;
            const y4 = center + innerRadius * sinEndAngle;

            const pathData = `M${x1},${y1} L${x2},${y2} A${outerRadius},${outerRadius} 0 0 1 ${x3},${y3} L${x4},${y4} A${innerRadius},${innerRadius} 0 0 0 ${x1},${y1} Z`;

            // Координаты кольца
            const rx1 = center + ringInner * cosStartAngle;
            const ry1 = center + ringInner * sinStartAngle;
            const rx2 = center + ringOuter * cosStartAngle;
            const ry2 = center + ringOuter * sinStartAngle;
            const rx3 = center + ringOuter * cosEndAngle;
            const ry3 = center + ringOuter * sinEndAngle;
            const rx4 = center + ringInner * cosEndAngle;
            const ry4 = center + ringInner * sinEndAngle;

            const ringPath = `M${rx1},${ry1} L${rx2},${ry2} A${ringOuter},${ringOuter} 0 0 1 ${rx3},${ry3} L${rx4},${ry4} A${ringInner},${ringInner} 0 0 0 ${rx1},${ry1} Z`;

            // Координаты текста
            const textRadius = (innerRadius + outerRadius) / 2;
            const labelX = center + textRadius * cosAngleRad;
            const labelY = center + textRadius * sinAngleRad;

            return {
                index: i,
                angleDeg,
                cosAngleRad,
                sinAngleRad,
                pathData,
                ringPath,
                labelX,
                labelY,
                color: status.color,
                label: status.label
            };
        });
    }, []);

    // Быстрые обработчики без async/await
    const handleSectorClick = useCallback((index) => {
        setSelectedIndex(index);
    }, []);

    const handleRingClick = useCallback((index) => {
        // Обновляем оба состояния одновременно для мгновенной отзывчивости
        const newLevel = (ringOpacityLevel[index] + 1) % (index === 0 ? 2 : 3);

        setRingAnimationState((prev) => {
            const newLevels = [...prev];
            newLevels[index] = newLevel;
            return newLevels;
        });

        setRingOpacityLevel((prev) => {
            const newLevels = [...prev];
            newLevels[index] = newLevel;
            return newLevels;
        });
    }, [ringOpacityLevel]);

    const handleMouseEnter = useCallback((index) => {
        setHoverIndex(index);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setHoverIndex(null);
    }, []);

    const handleExpandMouseEnter = useCallback(() => {
        setExpand(true);
        clearTimeout(expandTm);
    }, [expandTm]);

    const handleExpandMouseLeave = useCallback(() => {
        setExpandTm(setTimeout(() => { setExpand(false) }, 5000));
    }, []);

    const handleCenterClick = useCallback(() => {
        setShowOtherLabels(!showOtherLabels);
    }, [showOtherLabels]);

    // Упрощенный дебаунс
    const dispatchTimeoutRef = useRef(null);

    const debouncedDispatch = useCallback((filterData) => {
        if (dispatchTimeoutRef.current) {
            clearTimeout(dispatchTimeoutRef.current);
        }

        dispatchTimeoutRef.current = setTimeout(() => {
            dispatch(setFilter(filterData));
            // try {
            //     localStorage.setItem('dial', JSON.stringify({
            //         selectedIndex,
            //         // showOtherLabels,
            //         showSectorLabels,
            //         ringOpacityLevel
            //     }));
            // } catch (error) {
            //     console.warn('Failed to save to localStorage:', error);
            // }
        }, 300);
    }, [dispatch, selectedIndex, ringOpacityLevel]);

    // Первый этап
    // при клике на сектор когда сектор выдвинт отфильтровывается отображение связанных с этим замечаний по всей карте. 
    // На графике
    // В виджете сбоку
    // Красный и голубой (который я просил сделать) кружок на значке предписаний

    // showOtherLabels - Центральный кружок отвечает за отображение расстояний между опорами и скрытие расстояний. По умолчанию скрыто (серый)
    //     кольцо вокруг
    // По умолчанию полупрозрачное - означает что предписания отображаются в сокращенном виде
    // При первом клике когда и сектор становится полупрозрачным предписания данного статуса полностью скрываются, Опора которая была с предписаниями принимает вид будто предписаний не было и текст отображается согласно состоянию на кольце сектора «Все»
    // При втором клике кольцо и сектор становятся полноцветными и отображается предписание полноцветное
    // Третий клик возвращает всё как было.Опора с предписанием отображается своим цветом и предписание сокращенное.
    useEffect(() => {
        // const saved = JSON.parse(localStorage.getItem('dial'))
        // isMounted.current = true
        // if (!saved) return
        if (dialFilterRedux) {
            setSelectedIndex(dialFilterRedux.selectedLabel ?? 0)
            setRingOpacityLevel(dialFilterRedux.labelsOpacity ?? Array(statuses.length).fill(1))
        }
        // setSelectedIndex(saved.selectedIndex)

        // setShowOtherLabels(saved.showOtherLabels)
        setShowOtherLabels(showOtherLabelsRedux ?? true)
        // setShowSectorLabels(saved.showSectorLabels)
        // setRingOpacityLevel(saved.ringOpacityLevel)
        // Синхронизируем состояние анимации кольца
        // setRingAnimationState(saved.ringOpacityLevel || Array(statuses.length).fill(1))

    }, [])

    // Cleanup таймеров при размонтировании
    useEffect(() => {
        return () => {
            if (dispatchTimeoutRef.current) {
                clearTimeout(dispatchTimeoutRef.current);
            }
            if (expandTm) {
                clearTimeout(expandTm);
            }
        };
    }, [expandTm]);

    // Простой useEffect
    useEffect(() => {
        // if (!isMounted.current) return;
        debouncedDispatch({
            'dial': {
                labels: statuses.map(st => st.types),
                selectedLabel: selectedIndex,
                labelsOpacity: ringOpacityLevel,
                // otherLabels: showOtherLabels
            }
        });

    }, [selectedIndex, ringOpacityLevel, debouncedDispatch]);//showSectorLabels,

    useEffect(() => {
        // if (!isMounted.current) return;

        // debouncedDispatch({
        //     'dial': {
        //         labels: statuses.map(st => st.types),
        //         selectedLabel: selectedIndex,
        //         labelsOpacity: ringOpacityLevel,
        //         otherLabels: showOtherLabels
        //     }
        // });
        setTimeout(() => dispatch(setDataLayer({ name: 'viewFiderDistances', state: showOtherLabels })), 100)

    }, [showOtherLabels]);


    // Простой рендер с CSS transforms
    const renderElements = useMemo(() => {
        const hoverOffset = 16;

        return staticElements.map((element) => {
            const { index: i, angleDeg, cosAngleRad, sinAngleRad, pathData, ringPath, labelX, labelY, color, label } = element;

            const offset = selectedIndex === i || hoverIndex === i ? hoverOffset : 0;
            const dx = offset * cosAngleRad;
            const dy = offset * sinAngleRad;

            return (
                <React.Fragment key={i}>
                    {/* Кольцо - рендерим первым и с быстрой анимацией */}
                    <g
                        onClick={() => handleRingClick(i)}
                        style={{ cursor: 'pointer' }}
                    >
                        <path
                            d={ringPath}
                            fill={color}
                            fillOpacity={ringAnimationState[i] === 2 ? 1 : 0.3}
                            style={{
                                transform: `translate(${dx}px, ${dy}px)`,
                                transition: 'transform 0.2s ease-in-out, fill-opacity 0.15s ease-in-out'
                            }}
                        />
                    </g>

                    {/* Сектор - рендерим вторым с более медленной анимацией */}
                    <g
                        onMouseEnter={() => handleMouseEnter(i)}
                        onMouseLeave={handleMouseLeave}
                        onClick={() => handleSectorClick(i)}
                        style={{ cursor: "pointer" }}
                    >
                        <path
                            d={pathData}
                            fill={color}
                            opacity={ringOpacityLevel[i] === 0 ? 0.3 : 1}
                            style={{
                                transform: `translate(${dx}px, ${dy}px)`,
                                transition: 'transform 0.3s ease-in-out, opacity 0.4s ease-in-out'
                            }}
                        />
                        {/* {showSectorLabels && ( */}
                        <text
                            x={labelX}
                            y={labelY}
                            transform={`rotate(${angleDeg + 90}, ${labelX}, ${labelY})`}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize="12"
                            fill="#ffffff"
                            fontWeight="700"
                            fontFamily="Roboto"
                            style={{
                                transform: `translate(${dx}px, ${dy}px)`,
                                transition: 'transform 0.3s ease-in-out'
                            }}
                        >
                            {label}
                        </text>
                        {/* )} */}
                    </g>
                </React.Fragment>
            );
        });
    }, [staticElements, selectedIndex, hoverIndex, ringAnimationState, showSectorLabels, handleMouseEnter, handleMouseLeave, handleSectorClick, handleRingClick]);

    return (
        createPortal(
            <div className="status-dial-control"
                onMouseEnter={handleExpandMouseEnter}
                onMouseLeave={handleExpandMouseLeave}
                style={{
                    order: 1,
                    zIndex: 1200,
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: '5px',
                    pointerEvents: 'auto'
                }}
            >
                <svg className="status-dial-control-svg" width={isLargeScreen ? (expand ? 300 : 100) : (expand ? 200 : 70)} height={isLargeScreen ? (expand ? 300 : 100) : (expand ? 200 : 70)} viewBox="0 0 320 320" style={{ transition: "ease-in-out 0.3s" }}>
                    {renderElements}
                    <circle
                        cx="160"
                        cy="160"
                        r="24"
                        fill={showOtherLabels ? '#3b82f6' : '#9ca3af'}
                        onClick={handleCenterClick}
                        style={{
                            cursor: 'pointer',
                            transition: 'fill 0.3s ease-in-out'
                        }}
                    />
                    <foreignObject x="145" y="148" width="30" height="30" style={{ pointerEvents: 'none' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Material Icons', fontSize: '20px', color: 'white', pointerEvents: 'none' }}>
                            <span className="material-icons" style={{ fontSize: '24px', color: 'white' }}>expand</span>
                        </div>
                    </foreignObject>
                </svg>
            </div>
            , document.getElementById(window.innerWidth < 768 ? "map-controls-left-bottom-first-row-right" : "map-controls-right-bottom"))
    )
}

export default StatusDialControl;